import PropTypes from 'prop-types';
import { IoTimeOutline } from 'react-icons/io5';
import './GroupProgress.css';

/** Matches api/utils/season.js SEASON_CHAPTER_GOAL when the API omits a target. */
const GROUP_CHAPTER_GOAL = 75000;

const formatNumber = (value) => new Intl.NumberFormat().format(value ?? 0);

function chapterGoalFromStats(stats) {
  const goal = stats?.goals?.find((entry) => entry.key === 'total_chapters_read');
  const target = Number(goal?.targetValue);
  return Number.isFinite(target) && target > 0 ? target : GROUP_CHAPTER_GOAL;
}

/**
 * Compact group score: chapters against the season goal, readers who logged
 * chapters this season, Bible completions, and readers who finished the Bible.
 */
const GroupProgress = ({ stats, challenge }) => {
  if (!stats) return null;

  const chapters = stats.totalChaptersRead ?? 0;
  const goal = chapterGoalFromStats(stats);
  const readers = stats.totalReaders ?? 0;
  const completions = stats.completeBibleReads ?? 0;
  const finishers = stats.readersCompletedBible ?? 0;
  const percent = goal > 0 ? Math.round((chapters / goal) * 100) : 0;
  const barWidth = Math.min(percent, 100);
  const reached = chapters >= goal && goal > 0;

  return (
    <section className="group-progress" aria-label="Group reading progress">
      {challenge && (challenge.name || typeof challenge.daysRemaining === 'number') && (
        <div className="group-progress-meta">
          {challenge.name && <span className="group-progress-name">{challenge.name}</span>}
          {typeof challenge.daysRemaining === 'number' && (
            <span className="group-progress-countdown">
              <IoTimeOutline size={14} aria-hidden="true" />
              {challenge.daysRemaining === 0
                ? 'Last day'
                : `${challenge.daysRemaining} day${challenge.daysRemaining === 1 ? '' : 's'} left`}
            </span>
          )}
        </div>
      )}

      <div className="group-progress-row">
        <div className="group-progress-chapters">
          <p className="group-progress-figure">
            <span className="group-progress-value">{formatNumber(chapters)}</span>
            <span className="group-progress-goal"> / {formatNumber(goal)}</span>
          </p>
          <p className="group-progress-caption">
            chapters
            <span className="group-progress-percent"> · {percent}%</span>
          </p>
        </div>

        <div className="group-progress-readers">
          <p className="group-progress-figure">
            <span className="group-progress-value">{formatNumber(readers)}</span>
          </p>
          <p className="group-progress-caption">
            {readers === 1 ? 'reader' : 'readers'}
          </p>
        </div>

        <div className="group-progress-completions">
          <p className="group-progress-figure">
            <span className="group-progress-value">{formatNumber(completions)}</span>
          </p>
          <p className="group-progress-caption">
            <span className="group-progress-caption-full">
              {completions === 1 ? 'Bible completion' : 'Bible completions'}
            </span>
            <span className="group-progress-caption-short">
              {completions === 1 ? 'completion' : 'completions'}
            </span>
          </p>
        </div>

        <div className="group-progress-finishers">
          <p className="group-progress-figure">
            <span className="group-progress-value">{formatNumber(finishers)}</span>
          </p>
          <p className="group-progress-caption">
            <span className="group-progress-caption-full">finished the Bible</span>
            <span className="group-progress-caption-short">finished</span>
          </p>
        </div>
      </div>

      <div
        className="group-progress-track"
        role="progressbar"
        aria-valuenow={Math.min(chapters, goal)}
        aria-valuemin={0}
        aria-valuemax={goal}
        aria-label={`${formatNumber(chapters)} of ${formatNumber(goal)} chapters`}
      >
        <div
          className={`group-progress-fill${reached ? ' reached' : ''}`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </section>
  );
};

GroupProgress.propTypes = {
  stats: PropTypes.shape({
    totalChaptersRead: PropTypes.number,
    totalReaders: PropTypes.number,
    completeBibleReads: PropTypes.number,
    readersCompletedBible: PropTypes.number,
    goals: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      targetValue: PropTypes.number
    }))
  }),
  challenge: PropTypes.shape({
    name: PropTypes.string,
    daysRemaining: PropTypes.number
  })
};

export default GroupProgress;
